<!--
  Own top-level tab (Campaigns / Tasks -> a campaign -> "Bookings"),
  sibling to "Slot booking" (which owns configuring resources/hours).
  Calendar view -- Ramu (2026-09-04): "Bookings can be a calendar view kind
  of." A month grid (no external calendar library pulled in for this --
  plain CSS grid, matching how small the surface area actually is) with a
  booking count per day; clicking a day shows its full list below, with a
  Cancel action per upcoming booking that releases its capacity so the
  slot can be booked again. Navigating months re-fetches that month's
  range from the backend rather than trying to keep everything client-side.
-->
<template>
  <div>
    <header class="tw-reports-pane__head">
      <div>
        <p class="tw-reports-pane__eyebrow">Bookings</p>
        <h2 class="tw-reports-pane__title">{{ campaign?.name || 'Bookings' }}</h2>
        <p class="tw-reports-pane__sub">
          Real confirmed bookings the AI has made for this campaign, across every resource.
        </p>
      </div>
      <v-btn
        style="margin-left: auto; flex-shrink: 0"
        variant="tonal"
        color="primary"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="load"
      >
        Refresh
      </v-btn>
    </header>

    <div class="tw-cal-body">
      <div class="tw-cal-nav">
        <div class="tw-cal-nav__month">
          <v-btn icon="mdi-chevron-left" variant="text" density="comfortable" @click="prevMonth" />
          <span class="tw-cal-nav__label">{{ monthLabel }}</span>
          <v-btn icon="mdi-chevron-right" variant="text" density="comfortable" @click="nextMonth" />
        </div>
        <v-btn size="small" variant="tonal" @click="goToday">Today</v-btn>
      </div>

      <div v-if="loading" class="tw-adv-processing">
        <v-progress-circular indeterminate color="primary" size="18" width="2" />
        <span>Loading bookings…</span>
      </div>

      <div class="tw-cal-layout">
        <div class="tw-cal-grid" :class="{ 'tw-cal-grid--loading': loading }">
          <span v-for="d in WEEKDAY_LABELS" :key="d" class="tw-cal-grid__weekday">{{ d }}</span>
          <button
            v-for="(cell, idx) in calendarCells"
            :key="idx"
            type="button"
            class="tw-cal-cell"
            :class="{
              'tw-cal-cell--empty': !cell,
              'tw-cal-cell--today': cell && isToday(cell),
              'tw-cal-cell--selected': cell && isSelected(cell),
              'tw-cal-cell--has-bookings': cell && cellBookings(cell).length,
            }"
            :disabled="!cell"
            @click="cell && selectDay(cell)"
          >
            <span v-if="cell" class="tw-cal-cell__day">{{ cell }}</span>
            <span v-if="cell && cellBookings(cell).length" class="tw-cal-cell__count">
              {{ cellBookings(cell).length }}
            </span>
          </button>
        </div>

        <section class="tw-cal-detail">
          <span class="tw-cal-detail__title">{{ selectedDayLabel }}</span>

          <p v-if="!selectedDayBookings.length" class="tw-field-hint" style="margin-top: 6px">
            No bookings on this day.
          </p>
          <div v-else class="tw-sb-grid">
          <div
            v-for="booking in selectedDayBookings"
            :key="booking.booking_id"
            class="tw-sb-card"
            :class="{ 'tw-sb-card--past': isPast(booking) }"
          >
            <div class="tw-sb-card__head">
              <span class="tw-sb-card__resource">{{ booking.resource_name || 'Unknown resource' }}</span>
              <span class="tw-badge" :class="booking.status === 'CANCELLED' ? 'tw-badge--neutral' : 'tw-badge--success'">
                {{ booking.status === 'CANCELLED' ? 'Cancelled' : 'Booked' }}
              </span>
            </div>
            <p class="tw-sb-card__when">
              {{ formatTimeAmPm(booking.slot_start) }}–{{ formatTimeAmPm(booking.slot_end) }}
            </p>
            <p class="tw-sb-card__caller">
              <v-icon icon="mdi-phone-outline" size="13" />
              {{ booking.caller_number || '—' }}
            </p>
            <button
              v-if="booking.status !== 'CANCELLED' && !isPast(booking)"
              type="button"
              class="tw-sb-card__cancel"
              @click="pendingCancelId = booking.booking_id"
            >
              Cancel booking
            </button>
          </div>
        </div>
        </section>
      </div>

      <SlotConfirmDialog
        :model-value="!!pendingCancelId"
        icon="mdi-calendar-remove-outline"
        title="Cancel booking"
        subtitle="This frees the slot for someone else."
        :message="pendingCancelMessage"
        confirm-label="Yes, cancel booking"
        cancel-label="Keep it"
        danger
        :loading="!!cancellingId"
        @update:model-value="(v) => { if (!v) pendingCancelId = '' }"
        @confirm="pendingCancelBooking && cancelBooking(pendingCancelBooking)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { fetchSlotBookings, cancelSlotBooking } from '@/api/campaignsApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'
import { formatTimeAmPm } from '@/utils/format'
import SlotConfirmDialog from '@/components/Campaigns/SlotConfirmDialog.vue'

defineOptions({ name: 'CampaignSlotBookings' })

const props = defineProps({
  campaign: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['saved', 'error'])

const taskId = computed(() => String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim())

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const bookings = ref([])
const loading = ref(false)
const cancellingId = ref('')
// booking_id showing its SlotConfirmDialog -- was a native window.confirm(),
// then briefly an inline-expanding strip, before landing here (same history
// as pendingDeleteId in CampaignSlotBooking.vue).
const pendingCancelId = ref('')
const pendingCancelBooking = computed(() => bookings.value.find((b) => b.booking_id === pendingCancelId.value) || null)
const pendingCancelMessage = computed(() => {
  const b = pendingCancelBooking.value
  if (!b) return ''
  return `Cancel the booking for "${b.resource_name || 'this resource'}" at ${formatTimeAmPm(b.slot_start)}–${formatTimeAmPm(b.slot_end)}?`
})

const today = new Date()
const viewYear = ref(today.getFullYear())
const viewMonthIndex = ref(today.getMonth()) // 0-11
const selectedDate = ref(today.getDate())
// Which (year, month) the selection belongs to -- selecting a day, then
// navigating months, then coming back should still show that same day
// selected, not silently point at a different month's same day-number.
const selectedYear = ref(today.getFullYear())
const selectedMonthIndex = ref(today.getMonth())

const monthLabel = computed(() =>
  new Date(viewYear.value, viewMonthIndex.value, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
)

const calendarCells = computed(() => {
  const firstOfMonth = new Date(viewYear.value, viewMonthIndex.value, 1)
  const startWeekday = firstOfMonth.getDay()
  const daysInMonth = new Date(viewYear.value, viewMonthIndex.value + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let day = 1; day <= daysInMonth; day++) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
})

function dayKey(epochMs) {
  const d = new Date(Number(epochMs))
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

const bookingsByDay = computed(() => {
  const map = {}
  for (const b of bookings.value) {
    const key = dayKey(b.slot_start)
    if (!map[key]) map[key] = []
    map[key].push(b)
  }
  for (const key of Object.keys(map)) map[key].sort((a, b) => Number(a.slot_start) - Number(b.slot_start))
  return map
})

function cellBookings(day) {
  return bookingsByDay.value[`${viewYear.value}-${viewMonthIndex.value}-${day}`] || []
}

function isToday(day) {
  return viewYear.value === today.getFullYear() && viewMonthIndex.value === today.getMonth() && day === today.getDate()
}
function isSelected(day) {
  return viewYear.value === selectedYear.value && viewMonthIndex.value === selectedMonthIndex.value && day === selectedDate.value
}
function isPast(booking) {
  return Number(booking.slot_start) < Date.now()
}

function selectDay(day) {
  selectedDate.value = day
  selectedYear.value = viewYear.value
  selectedMonthIndex.value = viewMonthIndex.value
}

const selectedDayLabel = computed(() =>
  new Date(selectedYear.value, selectedMonthIndex.value, selectedDate.value).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }),
)
const selectedDayBookings = computed(() => bookingsByDay.value[`${selectedYear.value}-${selectedMonthIndex.value}-${selectedDate.value}`] || [])

watch(() => props.campaign, () => void load(), { immediate: true })

async function load() {
  if (!taskId.value) {
    bookings.value = []
    return
  }
  loading.value = true
  try {
    const rangeStart = new Date(viewYear.value, viewMonthIndex.value, 1).toISOString()
    const rangeEnd = new Date(viewYear.value, viewMonthIndex.value + 1, 1).toISOString()
    const response = await fetchSlotBookings({ task_id: taskId.value, range_start_iso: rangeStart, range_end_iso: rangeEnd })
    bookings.value = isApiSuccess(response) && Array.isArray(response.data) ? response.data : []
  } finally {
    loading.value = false
  }
}

function prevMonth() {
  if (viewMonthIndex.value === 0) {
    viewMonthIndex.value = 11
    viewYear.value -= 1
  } else {
    viewMonthIndex.value -= 1
  }
  void load()
}
function nextMonth() {
  if (viewMonthIndex.value === 11) {
    viewMonthIndex.value = 0
    viewYear.value += 1
  } else {
    viewMonthIndex.value += 1
  }
  void load()
}
function goToday() {
  viewYear.value = today.getFullYear()
  viewMonthIndex.value = today.getMonth()
  selectDay(today.getDate())
  void load()
}

async function cancelBooking(booking) {
  if (!booking?.booking_id || booking.status === 'CANCELLED') return
  pendingCancelId.value = ''
  cancellingId.value = booking.booking_id
  try {
    const response = await cancelSlotBooking({ booking_id: booking.booking_id })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to cancel booking'))
      return
    }
    booking.status = 'CANCELLED'
    emit('saved', 'Booking cancelled -- the slot is open again.')
  } catch (err) {
    emit('error', err)
  } finally {
    cancellingId.value = ''
  }
}
</script>

<style scoped>
.tw-cal-body {
  margin-top: 0.5rem;
}
.tw-adv-processing {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.75rem 0.9rem;
  margin-bottom: 12px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.06);
  border: 1px solid rgba(var(--v-theme-primary), 0.14);
  font-size: 0.85rem;
  color: var(--y-text);
}
.tw-field-hint {
  font-size: 0.8rem;
  color: var(--y-text-muted);
}

.tw-cal-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.tw-cal-nav__month {
  display: flex;
  align-items: center;
  gap: 4px;
}
.tw-cal-nav__label {
  font-weight: 650;
  font-size: 1rem;
  color: var(--y-text);
  min-width: 11rem;
  text-align: center;
}

/* Calendar on the left, day detail on the right -- side by side instead
   of stacked, so picking a day doesn't need scrolling down past the grid
   to see its bookings (Ramu, 2026-09-04). */
.tw-cal-layout {
  display: flex;
  align-items: flex-start;
  gap: 28px;
}

.tw-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  width: 320px;
  flex: 0 0 320px;
}
.tw-cal-grid--loading {
  opacity: 0.6;
}
.tw-cal-grid__weekday {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--y-text-muted);
  padding-bottom: 4px;
}
.tw-cal-cell {
  aspect-ratio: 1;
  border: 1px solid var(--y-border);
  border-radius: 8px;
  background: var(--y-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  font: inherit;
  padding: 0;
}
.tw-cal-cell:hover:not(:disabled) {
  border-color: rgba(var(--v-theme-primary), 0.5);
}
.tw-cal-cell--empty {
  visibility: hidden;
  cursor: default;
  border: none;
  background: transparent;
}
.tw-cal-cell__day {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--y-text);
}
.tw-cal-cell--today .tw-cal-cell__day {
  color: rgb(var(--v-theme-primary));
}
.tw-cal-cell--today {
  border-color: rgb(var(--v-theme-primary));
}
.tw-cal-cell--selected {
  background: rgba(var(--v-theme-primary), 0.12);
  border-color: rgb(var(--v-theme-primary));
}
.tw-cal-cell__count {
  font-size: 0.62rem;
  font-weight: 700;
  color: #fff;
  background: rgb(var(--v-theme-primary));
  border-radius: 999px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tw-cal-detail {
  flex: 1 1 0%;
  min-width: 0;
  padding-left: 24px;
  border-left: 1px solid var(--y-border);
}
.tw-cal-detail__title {
  font-size: 0.9rem;
  font-weight: 650;
  color: var(--y-text);
}

.tw-sb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 10px;
}
.tw-sb-card {
  border: 1px solid var(--y-border);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--y-surface);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tw-sb-card--past {
  opacity: 0.72;
}
.tw-sb-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.tw-sb-card__resource {
  font-weight: 650;
  font-size: 0.9rem;
  color: var(--y-text);
}
.tw-sb-card__when {
  margin: 0;
  font-size: 0.82rem;
  color: var(--y-text-secondary);
}
.tw-sb-card__caller {
  margin: 0;
  font-size: 0.78rem;
  color: var(--y-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
}
.tw-sb-card__cancel {
  margin-top: 6px;
  align-self: flex-start;
  border: 1px solid rgba(var(--v-theme-error), 0.4);
  background: transparent;
  color: rgb(var(--v-theme-error));
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.76rem;
  font-weight: 650;
  cursor: pointer;
}
.tw-sb-card__cancel:hover:not(:disabled) {
  background: rgba(var(--v-theme-error), 0.1);
}
.tw-sb-card__cancel:disabled {
  opacity: 0.6;
  cursor: default;
}
@media (max-width: 820px) {
  .tw-cal-layout {
    flex-direction: column;
  }
  .tw-cal-grid {
    width: 100%;
    max-width: 360px;
    flex: 0 0 auto;
  }
  .tw-cal-detail {
    width: 100%;
    padding-left: 0;
    border-left: none;
    padding-top: 1.25rem;
    border-top: 1px solid var(--y-border);
  }
}
</style>
